import type { JsonValue } from '@bufbuild/protobuf';
import { create, fromJson } from '@bufbuild/protobuf';
import type { Timestamp, TimestampJson } from '@bufbuild/protobuf/wkt';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { LatLngLiteral } from '@googlemaps/google-maps-services-js';
import { camelCase, camelCaseTransformMerge } from 'change-case';
import type { Duration, Interval } from 'date-fns';
import {
  addDays,
  addHours,
  addMinutes,
  addSeconds,
  differenceInMinutes,
  differenceInSeconds,
  intervalToDuration,
} from 'date-fns';
import { enumEquals, enumNumber } from './enums';
import type {
  AddressComponentJson,
  AddressComponentsJson,
  DenormalizedSurveyTask,
  ProjectServiceCreateResponse,
  ProjectServiceCreateResponseJson,
  ProjectSurveyTaskServiceCreateResponseJson,
  ProjectSurveyTaskTypeWithDescription,
  ProjectSurveyTaskTypeWithDescriptionJson,
  TimeTrackingServiceCreateResponse,
  TimeTrackingServiceCreateResponseJson,
} from './proto';
import {
  DenormalizedSurveyTaskSchema,
  DurationUnit,
  ProjectNameFormat,
  ProjectNameFormatSchema,
  ProjectSurveyTaskTypeWithDescriptionSchema,
} from './proto';

export const recordNotFound = '[NOT FOUND]';

export function formatProjectName(
  project: ProjectServiceCreateResponse | ProjectServiceCreateResponseJson,
): string {
  if (
    enumEquals(
      ProjectNameFormatSchema,
      project.nameFormat ?? 0,
      ProjectNameFormat.UNSPECIFIED,
    )
  ) {
    return project.name ?? '';
  }

  const { location } = project;

  if (!location) {
    return project.name ?? '';
  }

  switch (enumNumber(ProjectNameFormatSchema, project.nameFormat)) {
    case ProjectNameFormat.LOT_NUMBER:
      return `Lot ${location.lotNumber || '<Lot Number>'}, ${
        location.subdivision || '<Subdivision>'
      } SD, ${location?.address?.county || '<County>'} CTY, ${
        location?.address?.state || '<State>'
      }`.trim();
    case ProjectNameFormat.ADDRESS:
      return `${location?.address?.address || '<Address>'} ${
        location?.address?.city || '<City>'
      }, ${location?.address?.state || '<State>'}`.trim();
  }

  return project.name ?? '';
}

export interface GoogleMapsGeocodeAddressComponent {
  /**
   * The full text of the address component
   */
  long_name: string;
  /**
   * The abbreviated, short text of the given address component
   */
  short_name: string;
  /**
   * An array of strings denoting the type of this address component. A list
   * of valid types can be found <a
   * href="https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes">here</a>
   */
  types: string[];
}

export function convertGeocode(
  geoComponents: GoogleMapsGeocodeAddressComponent[],
): AddressComponentsJson {
  const validAddressFields = new Set<string>([
    'streetNumber',
    'route',
    'locality',
    'administrativeAreaLevel1',
    'administrativeAreaLevel2',
    'administrativeAreaLevel3',
    'country',
    'postalCode',
    'postalCodeSuffix',
    'neighborhood',
  ]);
  const components: Record<string, AddressComponentJson> = {};

  for (const component of geoComponents) {
    const fieldName = camelCase(component.types[0], {
      transform: camelCaseTransformMerge,
    });

    if (!validAddressFields.has(fieldName)) {
      continue;
    }

    components[fieldName] = {
      shortName: component.short_name,
      longName: component.long_name,
    };
  }

  return {
    ...components,
  };
}

export interface DurationValue {
  duration: number;
  durationUnit: DurationUnit;
}

/**
 * Normalizes the three shapes a `google.protobuf.Timestamp` reaches
 * application code in into a `Date`:
 *
 * - a `Date`, from a Formly datepicker, passed through untouched;
 * - an RFC3339 string, the `TimestampJson` form every NgRx selector and
 *   datasource in this repo emits;
 * - a `{ seconds, nanos }` message, the form a `fromJson`'d proto carries.
 *
 * Every caller must handle all three. Recognizing only the message form is
 * how the surveying list's Duration column came to render the current time
 * for every row after the protobuf-es v1 to v2 upgrade moved the store to
 * `*Json`: the omitted string branch fell through to a `new Date()` default
 * that read as plausible data rather than as a failure.
 */
export function timestampToDate(
  value: Date | Timestamp | TimestampJson | null | undefined,
): Date | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  if ('seconds' in value) {
    return timestampDate(value);
  }
  return undefined;
}

export function getDurationIntervalRaw(
  value: DurationValue,
  start?: Date | Timestamp | TimestampJson,
): Interval {
  const now = timestampToDate(start) ?? new Date();

  const interval: Interval = { start: now, end: now };

  switch (value.durationUnit) {
    case DurationUnit.SECONDS:
      interval.end = addSeconds(now, value.duration);
      break;
    case DurationUnit.DAYS:
      interval.end = addDays(now, value.duration);
      break;
    case DurationUnit.HOURS:
      interval.end = addHours(now, value.duration);
      break;
    case DurationUnit.MINUTES:
    default:
      interval.end = addMinutes(now, value.duration);
  }

  return interval;
}

export function getDurationInterval(value: DurationValue): Duration {
  return intervalToDuration(getDurationIntervalRaw(value));
}

export const defaultLatLng = {
  // default center to Bowling Green, KY
  lat: 36.976524,
  lng: -86.456017,
};

export function extendLatLngBounds(latLng: LatLngLiteral) {
  return [
    {
      lat: latLng.lat + 1,
      lng: latLng.lng - 1,
    },
    {
      lat: latLng.lat - 1,
      lng: latLng.lng + 1,
    },
    {
      lat: latLng.lat + 1,
      lng: latLng.lng + 1,
    },
    {
      lat: latLng.lat - 1,
      lng: latLng.lng - 1,
    },
  ];
}

export function getTotalSeconds(from: Date, to: Date): number {
  return Math.ceil(differenceInSeconds(to, from));
}

export function roundTotalHours(from: Date, to: Date): number {
  const minutes = Math.ceil(differenceInMinutes(to, from));
  return +(minutes / 60).toFixed(2);
}

export function roundBilledMinutesToHours(minutes: number): number {
  const remainder = minutes % 30 > 0 ? 30 : 0;
  const totalHours = (minutes - (minutes % 30) + remainder) / 60;
  return totalHours < 1 ? 1 : totalHours;
}

export function calculateBilledHours(from: Date, to: Date): number {
  const minutes = Math.ceil(differenceInMinutes(to, from));
  return roundBilledMinutesToHours(minutes);
}

export function denormalizeSurveyTask(
  surveyTask?: ProjectSurveyTaskServiceCreateResponseJson,
): DenormalizedSurveyTask {
  if (!surveyTask) {
    return create(DenormalizedSurveyTaskSchema, {});
  }

  const json = JSON.parse(JSON.stringify(surveyTask));

  return create(DenormalizedSurveyTaskSchema, {
    ...json,
    tasks: (json.tasks ?? []).map(
      (task: ProjectSurveyTaskTypeWithDescriptionJson) => {
        const normalizedTask = {
          ...task,
          customText: task.custom
            ? (surveyTask.customText ?? '')
            : (task.customText ?? ''),
        };

        return fromJson(
          ProjectSurveyTaskTypeWithDescriptionSchema,
          normalizedTask as unknown as JsonValue,
          {
            ignoreUnknownFields: true,
          },
        );
      },
    ),
  });
}

export function formatTimeTrackingBilledHours(
  value?:
    TimeTrackingServiceCreateResponse | TimeTrackingServiceCreateResponseJson,
  na = 'N/A',
): string | number {
  if (!value) {
    return na;
  }

  if (
    value.denormalized?.surveyTask?.tasks?.some((task) =>
      isSurveyTaskCustom(task),
    )
  ) {
    return value.billing?.billedHours ?? 0;
  }

  return na;
}

export function isTimeTrackingBilledToNA(
  value?:
    TimeTrackingServiceCreateResponse | TimeTrackingServiceCreateResponseJson,
  na = 'N/A',
): boolean {
  return formatTimeTrackingBilledTo(value, na) === na;
}

export function formatTimeTrackingBilledTo(
  value?:
    TimeTrackingServiceCreateResponse | TimeTrackingServiceCreateResponseJson,
  na = 'N/A',
): string {
  if (!value?.crewLeader) {
    return na;
  }

  if (
    !value.denormalized?.surveyTask?.tasks?.some((task) =>
      isSurveyTaskCustom(task),
    )
  ) {
    return na;
  }

  return value.denormalized?.billingClient?.name ?? '';
}

function isSurveyTaskCustom(
  task:
    | ProjectSurveyTaskTypeWithDescription
    | ProjectSurveyTaskTypeWithDescriptionJson,
): boolean {
  if ('type' in task) {
    return task.type?.case === 'custom';
  }

  return Boolean(task.custom);
}
